/**
 * ReconnectScreen - Loading screen for auto-reconnecting to active game session
 *
 * Per UX spec (lines 1311-1339):
 * - Shows portal animation
 * - Displays adventure name and party
 * - Brief loading screen while reconnecting
 * - Shows error state with retry option when game server is unavailable
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Swords } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { Button } from '@/components/ui/button';
import ManagedVideo from '@/components/common/ManagedVideo';
import { getVideoUrl } from '@/lib/media';

/**
 * Fallback video when no module hero media is available — prevents a pure-black
 * screen during adventure portal transitions and reconnects where localStorage
 * hero URLs were never populated (e.g. first-ever play, cleared storage).
 */
const FALLBACK_HERO_VIDEO = getVideoUrl('tavian.mp4');

/** Duel participant info for split portrait loading screen */
interface DuelParticipant {
  name?: string;
  imageUrl?: string;
}

interface ReconnectScreenProps {
  adventureName?: string;
  /** @deprecated Not currently displayed - reserved for future use */
  partyName?: string;
  /** Custom loading message (overrides default "Loading <adventure>...") */
  message?: string;
  heroImageUrl?: string;
  heroVideoUrl?: string;
  /** Duel participants for split portrait background (training sessions) */
  duelParticipants?: { player?: DuelParticipant; opponent?: DuelParticipant };
  /** Error message to display - when set, shows error state instead of loading */
  error?: string | null;
  /** Callback when user clicks retry - resets to lobby */
  onRetry?: () => void;
}

export function ReconnectScreen({
  adventureName,
  // Reserved for future UI enhancements
  partyName: _partyName,
  message,
  heroImageUrl,
  heroVideoUrl,
  duelParticipants,
  error,
  onRetry,
}: ReconnectScreenProps) {
  const isError = !!error;
  // Treat as "real" duel portraits only when at least one side has a usable
  // imageUrl. When both are missing (e.g. session.avatar_url not yet set on
  // the player, or a roster NPC without an avatar), falling through to the
  // standard hero/FALLBACK_HERO_VIDEO path renders the Tavian fallback video
  // instead of two bg-zinc-900 placeholders that read as a black screen.
  // Standard adventure path is unaffected — duelParticipants is undefined
  // there, so this evaluates to the same `false` as before.
  const hasDuelPortraits = !!(
    duelParticipants?.player?.imageUrl || duelParticipants?.opponent?.imageUrl
  );
  const [bgLoaded, setBgLoaded] = useState(false);
  const [playerImgFailed, setPlayerImgFailed] = useState(false);
  const [opponentImgFailed, setOpponentImgFailed] = useState(false);

  // Reset stale failure flags when the URL prop changes — ReconnectScreen
  // is kept mounted across `connecting → reconnecting → connected+!initialized`,
  // so a URL that failed on attempt 1 (say, a transient before lobby WS
  // delivered the resolved URL) would otherwise stay hidden behind the
  // zinc placeholder even after the prop updates with a valid URL.
  const playerImgUrl = duelParticipants?.player?.imageUrl;
  const opponentImgUrl = duelParticipants?.opponent?.imageUrl;
  useEffect(() => {
    setPlayerImgFailed(false);
  }, [playerImgUrl]);
  useEffect(() => {
    setOpponentImgFailed(false);
  }, [opponentImgUrl]);

  // [duel-loader] one-shot diagnostic so we can see what reached the loader.
  // Empty deps are intentional: this snapshots the values present at first
  // mount; subsequent updates land in the per-image onError handlers below.
  useEffect(() => {
    console.log('[duel-loader] ReconnectScreen mount', {
      hasDuelPortraits,
      duelParticipants,
      heroImageUrl,
      heroVideoUrl,
      isError,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Progressive cold-start messaging. The default `message` ("Opening a
  // portal to your adventure...") sits unchanged forever while the
  // game-server cold-starts (Gemini agent + module load + scene image
  // service can take 60-180s). Without progressive feedback the user
  // assumes the page is broken and reloads — which lands them right back
  // here. After a few seconds, swap to messages that telegraph "the server
  // is doing real work, this is expected to take a moment." Suppressed
  // when the caller passed a custom `message` (those are usually
  // user-driven contexts like "Creating your adventure..." which the
  // caller already controls). Also suppressed in error state.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (isError) return;
    const start = performance.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((performance.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isError]);
  const baseMessage = message || `Loading ${adventureName || 'adventure'}....`;
  let progressiveMessage = baseMessage;
  if (!message && !isError) {
    if (elapsedSeconds >= 90) {
      progressiveMessage = `Server is warming up—cold start can take ~2 min on first connect, then it's instant. Hold tight.`;
    } else if (elapsedSeconds >= 45) {
      progressiveMessage = `Almost there—loading scene assets and the game master...`;
    } else if (elapsedSeconds >= 20) {
      progressiveMessage = `Initializing game world...`;
    } else if (elapsedSeconds >= 8) {
      progressiveMessage = baseMessage.replace(/\.+$/, '...') + ' (loading)';
    }
  }

  // When no hero media is supplied (e.g. localStorage never populated on first
  // play, or was cleared), fall back to the lobby default video so the screen
  // is never pure black. Duel portraits own their own background.
  const resolvedHeroVideoUrl =
    heroVideoUrl ?? (!hasDuelPortraits && !heroImageUrl ? FALLBACK_HERO_VIDEO : undefined);
  // Whenever the fallback is active we know no image is underlaid (the guard
  // on line above requires `!heroImageUrl`) — so flip text contrast to "dark
  // bg" immediately without waiting on <ManagedVideo onCanPlay>. Relying on
  // the constant identity keeps the invariant local to the construction site.
  const isUsingFallback = resolvedHeroVideoUrl === FALLBACK_HERO_VIDEO;

  // Duel portraits + fallback video both imply a dark background immediately;
  // module-supplied media has to wait for onLoad/onCanPlay to flip the text.
  const hasDarkBg = hasDuelPortraits || isUsingFallback || bgLoaded;
  const textColor = hasDarkBg ? 'text-white/90' : 'text-foreground';
  const goldColor = hasDarkBg ? 'text-[#d4a574]' : 'text-accent-gold';

  return (
    <div
      className={`flex items-center justify-center min-h-screen relative ${hasDuelPortraits ? 'bg-zinc-950' : 'bg-background'}`}
    >
      {/* Duel split portrait background (training sessions) */}
      {hasDuelPortraits ? (
        <div className="absolute inset-0 w-full h-full flex">
          {/* Player portrait — left half */}
          <div className="flex-1 relative overflow-hidden">
            {duelParticipants?.player?.imageUrl && !playerImgFailed ? (
              <img
                src={duelParticipants.player.imageUrl}
                alt={duelParticipants.player.name ?? 'Player'}
                className="w-full h-full object-cover object-top"
                style={{ filter: 'brightness(0.7)' }}
                onError={() => {
                  console.warn(
                    '[duel-loader] player portrait failed to load',
                    duelParticipants?.player?.imageUrl
                  );
                  setPlayerImgFailed(true);
                }}
              />
            ) : (
              <div className="w-full h-full bg-zinc-900" />
            )}
            {/* Gradient toward center */}
            <div className="absolute inset-0 bg-gradient-to-l from-black/50 via-transparent to-black/20" />
            {/* Name at bottom-left */}
            <div className="absolute bottom-12 left-8 z-10">
              <span className="text-3xl font-bold text-white/90 drop-shadow-lg font-character">
                {duelParticipants?.player?.name ?? 'Player'}
              </span>
            </div>
          </div>

          {/* VS divider — spinning swords between portraits */}
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <Swords className="h-10 w-10 text-amber-500/80 drop-shadow-lg animate-spin" />
          </div>

          {/* Opponent portrait — right half */}
          <div className="flex-1 relative overflow-hidden">
            {duelParticipants?.opponent?.imageUrl && !opponentImgFailed ? (
              <img
                src={duelParticipants.opponent.imageUrl}
                alt={duelParticipants.opponent.name ?? 'Opponent'}
                className="w-full h-full object-cover object-top"
                style={{ filter: 'brightness(0.7)' }}
                onError={() => {
                  console.warn(
                    '[duel-loader] opponent portrait failed to load',
                    duelParticipants?.opponent?.imageUrl
                  );
                  setOpponentImgFailed(true);
                }}
              />
            ) : (
              <div className="w-full h-full bg-zinc-900" />
            )}
            {/* Gradient toward center */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/20" />
            {/* Name at bottom-right */}
            <div className="absolute bottom-12 right-8 z-10">
              <span className="text-3xl font-bold text-white/90 drop-shadow-lg font-character">
                {duelParticipants?.opponent?.name ?? 'Opponent'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Image Background (always shown as base layer/fallback) */}
          {heroImageUrl && (
            <>
              <img
                src={heroImageUrl}
                alt=""
                className="hidden"
                onLoad={() => !heroVideoUrl && setBgLoaded(true)}
              />
              <div
                className="absolute inset-0 w-full h-full"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${heroImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'top',
                }}
              />
            </>
          )}

          {/* Video Background (overlays image if video loads successfully) */}
          {resolvedHeroVideoUrl && (
            <ManagedVideo
              src={resolvedHeroVideoUrl}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.4)' }}
              onCanPlay={() => setBgLoaded(true)}
            />
          )}
        </>
      )}

      <div
        className={`relative z-10 text-center space-y-8 max-w-md px-4${hasDuelPortraits && !isError ? ' mt-[30vh]' : ''}`}
      >
        {isError ? (
          <>
            {/* Error Icon */}
            <AlertTriangle className="h-24 w-24 mx-auto text-destructive" />

            {/* Error Title */}
            <h2 className={`text-2xl font-bold transition-colors duration-700 ${textColor}`}>
              Failed to Start Adventure
            </h2>

            {/* Error Message */}
            <p className="text-destructive">{error}</p>

            {/* Retry Button */}
            {onRetry && (
              <Button onClick={onRetry} variant="default" size="lg" className="gap-2">
                <RefreshCw className="h-5 w-5" />
                Return to Lobby
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Big Loader Circle — only for non-duel (duel has it positioned above VS) */}
            {!hasDuelPortraits && (
              <AnkhSpinner
                size="lg"
                className={`mx-auto transition-colors duration-700 ${goldColor}`}
              />
            )}

            {/* Loading Message — progressive over time on cold-start */}
            <p className={`text-2xl transition-colors duration-700 ${textColor}`}>
              {progressiveMessage}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
